'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  IconExternalLink,
  IconCopy,
  IconAlertCircle,
} from '@tabler/icons-react';
import type { ProjectDocument } from '@/lib/projects/project-types';
import { formatDateNL } from '@/lib/projects/project-utils';

const PdfPreview = dynamic(
  () => import('./previews/PdfPreview').then((m) => m.PdfPreview),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading preview…
      </div>
    ),
  }
);

const DocxPreview = dynamic(
  () => import('./previews/DocxPreview').then((m) => m.DocxPreview),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
        Loading preview…
      </div>
    ),
  }
);

const docTypeLabel: Record<ProjectDocument['type'], string> = {
  pdf: 'PDF',
  docx: 'DOCX',
  link: 'Link',
};

const docTypeBadgeVariant: Record<
  ProjectDocument['type'],
  'destructive' | 'secondary' | 'outline'
> = {
  pdf: 'destructive',
  docx: 'secondary',
  link: 'outline',
};

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doc: ProjectDocument | null;
  projectTitle?: string;
  defaultMode?: 'preview' | 'details';
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  doc,
  defaultMode = 'preview',
}: DocumentPreviewDialogProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultMode);

  const canPreview = doc?.type === 'pdf' || doc?.type === 'docx';

  const hostname = (() => {
    if (!doc) return '';
    try {
      return new URL(doc.url).hostname;
    } catch {
      return doc.url;
    }
  })();

  const handleCopyUrl = async () => {
    if (!doc) return;
    try {
      await navigator.clipboard.writeText(doc.url);
      toast.success('URL copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[95vh] flex flex-col p-0 gap-0" style={{ width: '80vw', maxWidth: 'none' }}>
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-semibold leading-snug line-clamp-2">
                {doc.title}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5 truncate">
                {hostname}
              </DialogDescription>
            </div>
            <Badge
              variant={docTypeBadgeVariant[doc.type]}
              className="shrink-0 mt-0.5"
            >
              {docTypeLabel[doc.type]}
            </Badge>
          </div>
        </DialogHeader>

        <Separator />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col flex-1 min-h-0"
        >
          <TabsList className="mx-6 mt-3 mb-0 w-fit shrink-0">
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* Preview tab */}
          <TabsContent
            value="preview"
            className="flex-1 overflow-auto px-6 py-4 mt-0"
          >
            {canPreview ? (
              doc.type === 'pdf' ? (
                <PdfPreview url={doc.url} />
              ) : (
                <DocxPreview url={doc.url} />
              )
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                <IconAlertCircle className="size-10 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Preview not available for this file type.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Open the document directly to view its contents.
                  </p>
                </div>
                <Button size="sm" asChild>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink className="size-4 mr-1.5" />
                    Open in new tab
                  </a>
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Details tab */}
          <TabsContent
            value="details"
            className="flex-1 overflow-auto px-6 py-4 mt-0"
          >
            <div className="space-y-4">
              {/* URL row */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  URL
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs break-all flex-1 font-mono bg-muted rounded px-2 py-1.5">
                    {doc.url}
                  </p>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={handleCopyUrl}
                    aria-label="Copy URL"
                  >
                    <IconCopy className="size-3.5" />
                  </Button>
                </div>
              </div>

              {/* Type */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Type
                </p>
                <Badge variant={docTypeBadgeVariant[doc.type]}>
                  {docTypeLabel[doc.type]}
                </Badge>
              </div>

              {/* Source */}
              {doc.source && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Source
                  </p>
                  <p className="text-sm">{doc.source}</p>
                </div>
              )}

              {/* Published date */}
              {doc.publishedAt && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Published
                  </p>
                  <p className="text-sm">{formatDateNL(doc.publishedAt)}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Separator />

        {/* Footer */}
        <DialogFooter className="px-6 py-3 shrink-0 flex-row justify-between sm:justify-between gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={doc.url} target="_blank" rel="noopener noreferrer">
              <IconExternalLink className="size-4 mr-1.5" />
              Open in new tab
            </a>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
